import { BaseTextFieldProps, CircularProgress, TextField } from '@material-ui/core';
import { Autocomplete, RenderInputParams, UseAutocompleteProps } from '@material-ui/lab';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import gql from 'graphql-tag';
import React from 'react';
import { useQuery } from 'react-apollo';

type RoleSelectorProps<
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined
  > = Pick<BaseTextFieldProps, 'label' | 'variant' | 'fullWidth' | 'margin' | 'required' | 'error' | 'size' | 'helperText'> & 
  Omit<UseAutocompleteProps<Role, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'renderOption' | 'options' | 'getOptionLabel'> & 
  Pick<RoleQueryParams, 'isExternal'>;

export function RoleSelector<Multiple extends boolean | undefined, DisableClearable extends boolean | undefined, FreeSolo extends boolean | undefined>(props: RoleSelectorProps<Multiple, DisableClearable, FreeSolo>) {
    
    const roleQuery = useQuery<RolesQueryData, RoleQueryParams>(RolesQuery, {
        variables: {
            isExternal: props.isExternal,
        },
        fetchPolicy: 'cache-first',
    });
    
    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
            return {
                ...params.InputProps,
                endAdornment: (
                    <React.Fragment>
                        {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                        {params.InputProps.endAdornment}
                    </React.Fragment>
                ),
            };
        };
        
    const { label, variant, fullWidth, margin, required, error, size, multiple, value, helperText, ...autocompleteProps } = props;

    const normalizedValue = multiple ? (Array.isArray(value) ? value : []) : value;

    return (
        <Autocomplete<Role, Multiple, DisableClearable, FreeSolo>
            {...autocompleteProps}
            multiple={multiple}
            value={normalizedValue as typeof value}
            options={roleQuery.data?.roleList.role || []}
            getOptionLabel={(option) => option.rolename}
            getOptionSelected={(option, value) => option.roleIdString === value.roleIdString}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant={variant}
                    fullWidth={true}
                    margin="none"
                    required={required}
                    InputProps={getInputProps(params, roleQuery.loading)}
                    error={error}
                    helperText={helperText}
                    size={size}
                />
            )}
            // tslint:disable-next-line: jsx-no-lambda
            renderOption={(option, { inputValue }) => {
                const matches = match(option.rolename, inputValue);
                const parts = parse(option.rolename, matches);

                return (
                    <div>
                        {parts.map((part, index) => (
                            <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                {part.text}
                            </span>
                        ))}
                    </div>
                );
            }}
        />
    )
}

export interface RoleQueryParams {
    isExternal?: boolean;
}

export interface RolesQueryData {
    roleList: RoleList;
}

export interface RoleList {
    role:       Role[];
}

export interface Role {
    rolename:     string;
    isExternal:   boolean;
    roleIdString: string;
}

export enum Typename {
    RoleType = "RoleType",
}

export const RolesQuery = gql`
    query roles($isExternal: Boolean) {
        roleList(isExternal: $isExternal) {
            role {
                rolename
                isExternal
                roleIdString
            }
        }
    }
`;